import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createThread, getThreads } from "../../../../api/chat";
import { formatUserNowTime } from "../utils/chatHelpers";
import { useCompanyContext } from "../../../../context";

const THREAD_FETCH_COOLDOWN_MS = 500;

/* Shared deduped-fetch helper — same pattern as useChatSidebarData.
   If we extract this to a utils file later, both consumers can import.  */
const makeDedupedFetcher = (fetchFn) => {
  const state = { inFlight: null, lastCompletedAt: 0 };
  return () => {
    if (state.inFlight) return state.inFlight;
    if (Date.now() - state.lastCompletedAt < THREAD_FETCH_COOLDOWN_MS) {
      return Promise.resolve();
    }
    state.inFlight = fetchFn().finally(() => {
      state.lastCompletedAt = Date.now();
      state.inFlight = null;
    });
    return state.inFlight;
  };
};

const useChatThreads = ({
  threadType = "private",
  selectedModel = "",
} = {}) => {
  const [threads, setThreads] = useState([]);
  const [localThreads, setLocalThreads] = useState([]);
  const [currentThreadId, _setCurrentThreadId] = useState(null);
  const setCurrentThreadId = useCallback((v) => {
    console.trace("[setCurrentThreadId]", v);
    _setCurrentThreadId(v);
  }, []);
  const [persistedThreadIds, setPersistedThreadIds] = useState([]);

  const { companyKey } = useCompanyContext();

  const localThreadIds = useMemo(
    () => localThreads.map((t) => t.id),
    [localThreads],
  );

  const allThreads = useMemo(() => {
    const serverMapped = threads
      .map((t) => ({
        id: t.thread_id,
        label: t.title || "Thread",
        thread_type: t.thread_type || "private",
        isLocal: false,
      }))
      .filter((t) => t.id != null);

    return [
      ...localThreads,
      ...serverMapped.filter((s) => !localThreads.some((l) => l.id === s.id)),
    ];
  }, [threads, localThreads]);

  /* ── Deduped loadThreads ──
     The raw impl changes identity when `threadType` changes, so the
     deduped wrapper is rebuilt in an effect and called via ref.         */
  const loadThreadsImpl = useCallback(async () => {
    try {
      const res = await getThreads(0, threadType);
      const raw = res?.data?.threads || [];
      setThreads(raw);
    } catch (e) {
      console.error("Error loading threads:", e);
      setThreads([]);
    }
  }, [threadType]);

  const loadThreadsRef = useRef(null);
  if (!loadThreadsRef.current) {
    loadThreadsRef.current = makeDedupedFetcher(loadThreadsImpl);
  }
  useEffect(() => {
    loadThreadsRef.current = makeDedupedFetcher(loadThreadsImpl);
  }, [loadThreadsImpl]);

  const loadThreads = useCallback(() => loadThreadsRef.current(), []);

  const pendingLocalIdRef = useRef(null);
  const localThreadIdsRef_hook = useRef(localThreadIds);
  useEffect(() => {
    localThreadIdsRef_hook.current = localThreadIds;
  }, [localThreadIds]);

  const createNewThread = useCallback(() => {
    const existing = localThreads.find((t) => t.isLocal);
    if (existing) {
      setCurrentThreadId(existing.id);
      pendingLocalIdRef.current = null;
      return existing.id;
    }

    if (pendingLocalIdRef.current) {
      setCurrentThreadId(pendingLocalIdRef.current);
      return pendingLocalIdRef.current;
    }

    const id = crypto.randomUUID();
    pendingLocalIdRef.current = id;
    const newLocal = {
      id,
      label: "New thread",
      isLocal: true,
      loading: true,
      thread_type: threadType,
    };
    setLocalThreads((prev) => [newLocal, ...prev]);
    localThreadIdsRef_hook.current = [id, ...localThreadIdsRef_hook.current];
    setCurrentThreadId(id);
    return id;
  }, [localThreads, threadType]);

  const markThreadPersisted = useCallback((threadId) => {
    if (pendingLocalIdRef.current === threadId) {
      pendingLocalIdRef.current = null;
    }
    setLocalThreads((prev) => prev.filter((t) => t.id !== threadId));
  }, []);

  const isLocalThread = useCallback(
    (threadId) => localThreadIds.includes(threadId),
    [localThreadIds],
  );

  const ensureThreadExists = useCallback(
    async (threadId, firstMessage, threadAge) => {
      if (!threadId || persistedThreadIds.includes(threadId)) return;

      const knownLocal = localThreadIdsRef_hook.current.includes(threadId);

      if (!knownLocal && !isLocalThread(threadId)) {
        setPersistedThreadIds((prev) =>
          prev.includes(threadId) ? prev : [...prev, threadId],
        );
        return;
      }

      try {
        // `selectedModel` only applies to public threads — it's the LiteLLM
        // model key for that conversation. Private threads use our own
        // backend agents (MAIN / DISCOVERY) and must NOT carry a model
        // field, otherwise a stale public-model key (seeded from
        // getEnabledPublicModels on app mount) leaks into the private
        // thread row at creation time.
        //
        // Branching the call rather than passing `selectedModel : undefined`
        // because `createThread`'s downstream handling of `undefined` is
        // opaque; literally not passing the arg matches the other
        // createThread call sites (e.g. last_chat update in useChatStream)
        // and produces the smallest, most predictable request body.
        const res =
          threadType === "public"
            ? await createThread(
                threadId,
                firstMessage,
                threadAge,
                formatUserNowTime(),
                threadType,
                selectedModel,
              )
            : await createThread(
                threadId,
                firstMessage,
                threadAge,
                formatUserNowTime(),
                threadType,
              );

        const title = res?.data?.thread_data?.title || "New thread";
        setLocalThreads((prev) =>
          prev.map((t) =>
            t.id === threadId ? { ...t, label: title, loading: false } : t,
          ),
        );

        setPersistedThreadIds((prev) =>
          prev.includes(threadId) ? prev : [...prev, threadId],
        );

        // Load server threads, then remove local copy
        await loadThreads();
        markThreadPersisted(threadId);

        /* ── Notify sidebar exactly once per real creation ──
           Previously MainChat fired `sidebar:refresh-threads` on every
           `allThreads` change, causing redundant fetches. Now we fire
           here — the only place that warrants a sidebar sync.           */
        window.dispatchEvent(new CustomEvent("sidebar:refresh-threads"));
      } catch (e) {
        console.error("ensureThreadExists failed:", e);
        setLocalThreads((prev) =>
          prev.map((t) => (t.id === threadId ? { ...t, loading: false } : t)),
        );
      }
    },
    [
      persistedThreadIds,
      isLocalThread,
      markThreadPersisted,
      loadThreads,
      threadType,
      selectedModel,
    ],
  );

  useEffect(() => {
    loadThreads();
  }, [loadThreads, companyKey]);

  return {
    allThreads,
    currentThreadId,
    setCurrentThreadId,
    localThreadIds,
    loadThreads,
    createNewThread,
    markThreadPersisted,
    ensureThreadExists,
    isLocalThread,
  };
};

export default useChatThreads;
